import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { SettingIconComponent } from '../../../assets/icons';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../components';
import { useForm, useModal } from '../../../hooks';
import {
  editOperatorProfileStart,
  resetCurrentOperator,
  setCurrentOperator,
  fetchOperatorsStart,
} from 'redux/operators/operators.actions';
import { fetchWebsitesStart } from '../../../redux/websites/websites.actions';
import {
  EditOperatorModalInputs,
  EditOperatorModalWebsites,
} from '../../OperatorsHeader/';
import { validateEditOperator } from './validateEditOperator';
import styles from './EditOperatorModal.module.scss';
import OperatorAvatar from '../../OperatorAvatar';
import useTranslation from 'hooks/useTranslation';
import { LineWave } from 'react-loader-spinner';
import { isValidIranMobileNum } from 'utils/validation';

const getCurrentOperator = ({ operatorsData }) => operatorsData.currentOperator;
const getOperators = ({ operatorsData }) => operatorsData.operators.data;
const getProfile = ({ userData }) => userData.profile;
const getWebsites = ({ websitesData }) => websitesData.websites;

const EditOperatorModal = ({ operatorId, isWeeklyChosen, isProfile }) => {
  const dispatch = useDispatch();

  const operators = useSelector(getOperators);
  const currentOperator = useSelector(getCurrentOperator);
  const profile = useSelector(getProfile);
  const websites = useSelector(getWebsites);

  const {
    position,
    fullName,
    email,
    mobile,
    accessLevel,
    websiteIds,
    imageUrl,
    isCreator,
    accepted,
    workTime,
  } = currentOperator || {};
  const [visible, toggle] = useModal();
  const [selectedAccessLevel, setSelectedAccessLevel] = useState(accessLevel);
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const { isCreatorAdmin } = profile;
  const { getTranslation } = useTranslation('OPERATORS');
  const { t } = getTranslation();

  const handleEditOperator = (values) => {
    const isMobileValidated = profile.mobile === values.mobile;
    const isAccountEqOperator = profile.userId === operatorId;
    const operator = {
      values,
      selectedAccessLevel,
      selectedWebsiteIds: selectedWebsiteIds.join(','),
      operatorId,
      uploadedImage,
      accepted,
      workTime,
      isCreator,
      currentImageUrl,
      isMobileValidated,
      isAccountEqOperator,
      handleToggleModal,
    };
    dispatch(editOperatorProfileStart(operator));
  };

  const { values, errors, handleChange, handleSubmit, setDefaultValues } =
    useForm(validateEditOperator, handleEditOperator, {
      position: '',
      fullName: '',
      email: '',
      mobile: '',
      password: '',
      confirmPassword: '',
    });

  const isMobileValidated = profile.mobile === values.mobile;
  const isAccountEqOperator = profile.userId === operatorId;

  const loadingStatus = !operators;

  /**
   * NOTE: why doing this?
   * After some time it was decided to save values only in +98********** but
   * because some older values were in 09********* format it produced some bugs in Validation.
   * So it was decided to have a conversion on the phone numbers saved in formats other than valid formats
   */
  const getConvertedMobileNumber = (number) => {
    const convertedNumber = String(number).trim();

    if (
      isValidIranMobileNum(convertedNumber) &&
      convertedNumber.startsWith('0')
    ) {
      return '+98' + convertedNumber.slice(1);
    }

    return number;
  };

  const convertedMobile = getConvertedMobileNumber(mobile);

  const setFormData = () => {
    setDefaultValues({
      position,
      fullName,
      email,
      mobile: convertedMobile,
      password: '',
      confirmPassword: '',
    });
    setSelectedAccessLevel(accessLevel);
    setSelectedWebsiteIds(websiteIds);
    setCurrentImageUrl(imageUrl);
  };

  useEffect(() => {
    setFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperator]);

  useEffect(() => {
    if (!visible) {
      dispatch(resetCurrentOperator());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleToggleModal = (event) => {
    if (event) event.preventDefault();
    setFormData();
    toggle();
    setUploadedImage(null);
    if (!visible && websites.data && websites.data.length === 0) {
      dispatch(fetchWebsitesStart());
    }
  };

  const handleChangeImage = (event) => {
    const file = event.target.files[0];
    setUploadedImage(file);
  };

  const handleRemoveImage = (event) => {
    if (event) event.preventDefault();
    if (uploadedImage) {
      setUploadedImage(null);
      return;
    }
    setCurrentImageUrl('');
  };

  const renderImageUpload = (
    <div className={styles.imageUploadWrapper}>
      <OperatorAvatar
        avatarId={operatorId}
        imageUrl={
          uploadedImage ? URL.createObjectURL(uploadedImage) : currentImageUrl
        }
        fullName={fullName}
        wrapperClass={styles.avatarWrapper}
      />
      <span className={styles.hint}>{t('OPERATOR_MODAL_AVATAR_HINT')}</span>

      <label className={styles.customeFileUpload}>
        <input
          type='file'
          accept='image/gif, image/jpeg, image/png'
          onChange={handleChangeImage}
        />
        {t('OPERATOR_MODAL_AVATAR_UPLOAD_IMAGE')}
      </label>
      {(currentImageUrl || uploadedImage) && (
        <a
          href='#/'
          className={clsx('btn', styles.removeImagebutton)}
          onClick={handleRemoveImage}
        >
          {t('OPERATOR_MODAL_AVATART_DELETE_IMAGE')}
        </a>
      )}
    </div>
  );
  const renderAccessLevelSection = (
    <>
      <h5 className='modal__container__body__invite__form__title'>
        {t('OPERATOR_MODAL_ACCESS_LEVEL')}
      </h5>
      <div className='modal__container__body__invite__form__permission grid'>
        <div className='select'>
          <select
            name='permission'
            id='permission'
            value={selectedAccessLevel}
            onChange={(event) => setSelectedAccessLevel(event.target.value)}
          >
            <option value='user' defaultValue>
              {t('OPERATOR')}
            </option>
            {isCreatorAdmin && <option value='admin'>{t('ADMIN')}</option>}
          </select>
        </div>
      </div>
    </>
  );

  const renderModalTogglerButton = (
    <>
      {isProfile && (
        <a href='#/' onClick={handleToggleModal}>
          {t('USER_PROFILE')}
        </a>
      )}
      {!isProfile && (
        <a href='#/' className='flex' onClick={handleToggleModal}>
          {/* <img src={SettingIcon} alt='' /> */}
          <SettingIconComponent />
          <p>
            {isAccountEqOperator
              ? t('PROFILE_SETTINGS')
              : t('OPERATORS_SETTINGS')}
          </p>
        </a>
      )}
    </>
  );

  const renderFooterActions = (
    <div className='modal__container__footer__left'>
      <a
        href='#/'
        className='btn btn-tertiary'
        id='closeModal'
        onClick={handleToggleModal}
      >
        {t('OPERATOR_MODAL_CANCEL')}
      </a>
      {(!isAccountEqOperator || (isAccountEqOperator && isMobileValidated)) && (
        <a href='#/' className='btn btn-primary' onClick={handleSubmit}>
          {t('OPERATOR_MODAL_SAVE_CHANGES')}
        </a>
      )}
      {isAccountEqOperator && !isMobileValidated && (
        <a href='#/' className='btn btn-primary' onClick={handleSubmit}>
          {t('OPERATOR_MODAL_SEND_CONFIRM_CODE')}
        </a>
      )}
    </div>
  );

  const renderLoader = (
    <LineWave
      height={120}
      width={120}
      color='rgb(177, 14, 132)'
      ariaLabel='line-wave'
      wrapperStyle={{}}
      wrapperClass=''
      visible={true}
    />
  );

  const renderModalBody = loadingStatus ? (
    <div className='flex flex-jc-c '>{renderLoader}</div>
  ) : (
    <div className='modal__container__body__invite'>
      <div className='modal__container__body__invite__form'>
        <form className='' noValidate onSubmit={handleSubmit}>
          {renderImageUpload}
          <EditOperatorModalInputs
            errors={errors}
            values={values}
            handleChange={handleChange}
            isInviteOperator={false}
          />
          <EditOperatorModalWebsites
            selectedWebsiteIds={selectedWebsiteIds}
            setSelectedWebsiteIds={setSelectedWebsiteIds}
          />
          {!isCreator && !isAccountEqOperator && renderAccessLevelSection}
        </form>
      </div>
    </div>
  );

  useEffect(() => {
    if (visible && !operators) {
      dispatch(fetchOperatorsStart());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && operators) {
      dispatch(setCurrentOperator({ operatorId, isWeeklyChosen }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operators, visible]);

  return (
    <>
      {renderModalTogglerButton}
      <Modal visible={visible}>
        <ModalHeader
          title={
            isAccountEqOperator
              ? t('PROFILE_SETTINGS')
              : t('OPERATORS_SETTINGS')
          }
        />
        <ModalBody>{renderModalBody}</ModalBody>
        <ModalFooter>{renderFooterActions}</ModalFooter>
      </Modal>
    </>
  );
};

export default React.memo(EditOperatorModal);
